import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';

/**
 * Thin global banner that warns about connectivity:
 *   - RED   "No internet connection"   when the browser is offline
 *   - YELLOW "Slow internet connection" when the connection is 2g / slow-2g
 *   - nothing at all when the connection is fine
 *
 * Uses the standard online/offline events plus the Network Information API
 * (navigator.connection) where available. Both are read defensively so the
 * component is a no-op on browsers that don't support them (it just never
 * shows the yellow bar there).
 */

type NetState = 'ok' | 'slow' | 'offline';

const getConnection = (): any =>
  (navigator as any).connection ||
  (navigator as any).mozConnection ||
  (navigator as any).webkitConnection ||
  null;

const readState = (): NetState => {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return 'offline';
  const conn = getConnection();
  if (conn) {
    const et = conn.effectiveType;
    if (et === 'slow-2g' || et === '2g') return 'slow';
    // downlink is in Mbps; treat a very low estimate as slow too.
    if (typeof conn.downlink === 'number' && conn.downlink > 0 && conn.downlink < 0.5) return 'slow';
  }
  return 'ok';
};

const NetworkStatusBar: React.FC = () => {
  const [state, setState] = useState<NetState>('ok');

  useEffect(() => {
    const update = () => setState(readState());
    update();

    window.addEventListener('online', update);
    window.addEventListener('offline', update);
    const conn = getConnection();
    if (conn && typeof conn.addEventListener === 'function') {
      conn.addEventListener('change', update);
    }
    return () => {
      window.removeEventListener('online', update);
      window.removeEventListener('offline', update);
      if (conn && typeof conn.removeEventListener === 'function') {
        conn.removeEventListener('change', update);
      }
    };
  }, []);

  if (state === 'ok') return null;

  const offline = state === 'offline';
  return (
    <Box
      role="status"
      aria-live="polite"
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: (theme) => theme.zIndex.snackbar + 1,
        height: 24,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '0.72rem',
        fontWeight: 600,
        letterSpacing: 0.3,
        color: offline ? '#fff' : '#5a4500',
        bgcolor: offline ? '#d32f2f' : '#ffd54f',
      }}
    >
      {offline ? 'No internet connection' : 'Slow internet connection'}
    </Box>
  );
};

export default NetworkStatusBar;
